import { lazy, type ComponentType } from 'react';

const SESSION_KEY = 'atriva.chunk-reload-attempted';
const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i;

function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);
}

/**
 * Safely read the reload flag from sessionStorage.
 * Returns `true` if reload already attempted, `false` otherwise.
 * On storage failure (Safari private mode, disabled storage), returns `true`
 * to *prevent* reload — we'd rather surface the error than risk a reload loop
 * we cannot detect.
 */
function reloadAlreadyAttempted(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) !== null;
  } catch {
    return true;
  }
}

/**
 * Safely mark that a reload was attempted. Returns `true` on success,
 * `false` if storage threw — in which case the caller must skip the reload
 * (we cannot guarantee idempotency).
 */
function markReloadAttempted(): boolean {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Wraps React.lazy with two safeguards against stale chunks after deploys:
 *
 * 1. Single retry — handles transient CDN flakiness (S3 propagation, nginx reload).
 * 2. Auto-reload once if retry also fails AND error is still a chunk error —
 *    fetches fresh index.html with new hashes. Uses sessionStorage so we never
 *    enter an infinite reload loop. If sessionStorage is unavailable
 *    (Safari private mode, blocked storage), reload is skipped entirely and
 *    the error propagates to the ErrorBoundary.
 *
 * Non-chunk errors are always re-thrown immediately, so genuine bugs surface.
 */
export function lazyWithRetry<T extends ComponentType<object>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;

      // Retry once — covers transient CDN race
      try {
        return await factory();
      } catch (retryError) {
        // If retry failed for a non-chunk reason (e.g. evaluation/runtime error
        // in the freshly fetched module), surface that real failure instead of
        // hiding it behind a reload.
        if (!isChunkLoadError(retryError)) throw retryError;

        // Real stale chunk: hard reload to fetch new index.html, but only once
        // per session (and only if storage is available to enforce that guard).
        if (!reloadAlreadyAttempted() && markReloadAttempted()) {
          window.location.reload();
          // Never-resolving promise so React doesn't render anything before reload
          return new Promise(() => {}) as Promise<{ default: T }>;
        }

        throw retryError;
      }
    }
  });
}
